import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Parse an ISO date string (YYYY-MM-DD) without timezone shift.
 */
export function parseLocalDate(dateStr: string): Date {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/**
 * Strip clerical titles from a preacher's name for display purposes.
 */
export function stripPreacherTitle(name: string): string {
  if (!name) return '';
  return name
    .replace(/^(Pr\.?|Pb\.?|Rev\.?|Ps\.?|Pastor|Pastora|Reverendo|Reverenda|Presbítero|Presbítera|Apóstolo|Apóstola|Bispo|Bispa|Missionário|Missionária|Diácono|Diác\.?|Dr\.?)\s+/i, '')
    .trim();
}

/**
 * Extract the book name from a biblical text reference.
 * Handles numbered books like "1 Coríntios 13:1-7" → "1 Coríntios"
 */
export function extractBookName(biblicalText: string): string {
  if (!biblicalText) return '';
  const trimmed = biblicalText.trim();
  const match = trimmed.match(/^(\d\s*[A-Za-zÀ-ú]+)/);
  if (match) return match[1].replace(/(\d)\s*/, '$1 ');
  const firstWord = trimmed.split(/[\s:,;.]+/)[0];
  return firstWord || '';
}

/**
 * Canonical order of the 66 books of the Protestant Bible (Portuguese).
 */
export const BIBLE_BOOKS_ORDER: string[] = [
  // Antigo Testamento
  'Gênesis', 'Êxodo', 'Levítico', 'Números', 'Deuteronômio',
  'Josué', 'Juízes', 'Rute', '1 Samuel', '2 Samuel',
  '1 Reis', '2 Reis', '1 Crônicas', '2 Crônicas', 'Esdras',
  'Neemias', 'Ester', 'Jó', 'Salmos', 'Provérbios',
  'Eclesiastes', 'Cânticos', 'Isaías', 'Jeremias', 'Lamentações',
  'Ezequiel', 'Daniel', 'Oséias', 'Joel', 'Amós',
  'Obadias', 'Jonas', 'Miquéias', 'Naum', 'Habacuque',
  'Sofonias', 'Ageu', 'Zacarias', 'Malaquias',
  // Novo Testamento
  'Mateus', 'Marcos', 'Lucas', 'João', 'Atos',
  'Romanos', '1 Coríntios', '2 Coríntios', 'Gálatas', 'Efésios',
  'Filipenses', 'Colossenses', '1 Tessalonicenses', '2 Tessalonicenses',
  '1 Timóteo', '2 Timóteo', 'Tito', 'Filemom', 'Hebreus',
  'Tiago', '1 Pedro', '2 Pedro', '1 João', '2 João', '3 João',
  'Judas', 'Apocalipse',
];

/**
 * Returns the canonical index of a book name (for sorting). Unknown books go to the end.
 */
export function bibleBookIndex(name: string): number {
  if (!name) return 999;
  const normalized = name.trim().toLowerCase();
  const idx = BIBLE_BOOKS_ORDER.findIndex(b => b.toLowerCase() === normalized);
  return idx === -1 ? 999 : idx;
}

/**
 * Build a Supabase Storage image transformation URL.
 * For Supabase public storage URLs, swaps `/object/public/` for
 * `/render/image/public/` and appends width/quality/format params,
 * letting the CDN serve a smaller WebP variant. Returns the original
 * URL for non-Supabase Storage URLs (e.g. external CDNs).
 */
export function getOptimizedImageUrl(
  url: string | null | undefined,
  opts: { width?: number; quality?: number } = {}
): string {
  if (!url) return '';
  if (!url.includes('/storage/v1/object/public/')) return url;
  try {
    const u = new URL(url);
    u.pathname = u.pathname.replace('/storage/v1/object/public/', '/storage/v1/render/image/public/');
    if (opts.width) u.searchParams.set('width', String(opts.width));
    u.searchParams.set('quality', String(opts.quality ?? 75));
    u.searchParams.set('resize', 'cover');
    return u.toString();
  } catch {
    return url;
  }
}
